<template>
  <div v-if="assignments.length" class="space-y-4 mb-6">
    <div
      v-for="assignment in assignments"
      :key="assignment.fieldId"
      class="space-y-2"
    >
      <UFormField
        :label="fieldLabel(assignment)"
        :required="assignment.isRequired"
        :error="errors[assignment.fieldId]"
      >
        <template v-if="assignment.field?.fieldType === 'textarea'">
          <UTextarea
            :model-value="stringValue(assignment.fieldId)"
            :rows="3"
            @update:model-value="(value) => setValue(assignment.fieldId, value)"
          />
        </template>

        <template v-else-if="assignment.field?.fieldType === 'number'">
          <UInput
            :model-value="numberValue(assignment.fieldId)"
            type="number"
            @update:model-value="(value) => setValue(assignment.fieldId, value === '' ? null : Number(value))"
          />
        </template>

        <template v-else-if="assignment.field?.fieldType === 'date'">
          <ComplexFieldDatePicker
            :model-value="stringValue(assignment.fieldId) || null"
            placeholder="Selectează data"
            @update:model-value="(value) => setValue(assignment.fieldId, value || null)"
          />
        </template>

        <template v-else-if="assignment.field?.fieldType === 'date_range'">
          <ComplexFieldDatePicker
            range
            :model-value="rangeValue(assignment.fieldId)"
            :min-days="dateRangeMinDays(assignment)"
            placeholder="Selectează perioada"
            @update:model-value="(value) => setRangeValue(assignment.fieldId, value)"
          />
        </template>

        <template v-else-if="assignment.field?.fieldType === 'time'">
          <UInput
            :model-value="stringValue(assignment.fieldId)"
            type="time"
            @update:model-value="(value) => setValue(assignment.fieldId, value)"
          />
        </template>

        <template v-else-if="assignment.field?.fieldType === 'select' || assignment.field?.fieldType === 'radio'">
          <USelectMenu
            :model-value="stringValue(assignment.fieldId) || undefined"
            :items="optionItems(assignment)"
            value-key="value"
            label-key="label"
            class="w-full"
            @update:model-value="(value) => setValue(assignment.fieldId, value)"
          />
        </template>

        <template v-else-if="assignment.field?.fieldType === 'multi_select'">
          <USelectMenu
            :model-value="arrayValue(assignment.fieldId)"
            :items="optionItems(assignment)"
            value-key="value"
            label-key="label"
            multiple
            class="w-full"
            @update:model-value="(value) => setValue(assignment.fieldId, normalizeComplexFieldOptions(value))"
          />
        </template>

        <template v-else-if="assignment.field?.fieldType === 'checkbox'">
          <label class="flex items-center gap-2 text-sm cursor-pointer">
            <UCheckbox
              :model-value="Boolean(values[assignment.fieldId])"
              @update:model-value="(value) => setValue(assignment.fieldId, Boolean(value))"
            />
            {{ assignment.field.name }}
          </label>
        </template>

        <template v-else-if="assignment.field?.fieldType === 'file'">
          <ComplexFieldFileUpload
            :model-value="stringValue(assignment.fieldId) || null"
            @update:model-value="(value) => setValue(assignment.fieldId, value)"
          />
        </template>

        <template v-else>
          <UInput
            :model-value="stringValue(assignment.fieldId)"
            :type="inputType(assignment.field?.fieldType)"
            @update:model-value="(value) => setValue(assignment.fieldId, value)"
          />
        </template>
      </UFormField>

      <p v-if="priceHint(assignment)" class="text-xs text-muted">
        {{ priceHint(assignment) }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computeCustomFieldsPriceDelta,
  getFieldUnitCount,
  isEmptyCustomFieldValue,
  normalizeComplexFieldOptions,
  resolveAssignmentPricing,
  type CustomFieldValueInput,
  type ProductComplexFieldAssignment,
} from '~/utils/complexProductFields'

const props = defineProps<{
  assignments: ProductComplexFieldAssignment[]
  /** Product unit price before complex-field deltas (for percent mode). */
  basePrice?: number
}>()

const emit = defineEmits<{
  change: [values: CustomFieldValueInput[], priceDelta: number]
}>()

const values = reactive<Record<number, unknown>>({})
const errors = reactive<Record<number, string>>({})

watch(() => props.basePrice, () => {
  emitChange()
})

function fieldLabel(assignment: ProductComplexFieldAssignment) {
  if (assignment.field?.fieldType === 'checkbox') return ''
  return assignment.field?.name || `Câmp #${assignment.fieldId}`
}

function optionItems(assignment: ProductComplexFieldAssignment) {
  return normalizeComplexFieldOptions(assignment.field?.options).map((option) => ({
    label: option,
    value: option,
  }))
}

function stringValue(fieldId: number) {
  const value = values[fieldId]
  return typeof value === 'string' ? value : value == null ? '' : String(value)
}

function numberValue(fieldId: number) {
  const value = values[fieldId]
  return value == null ? '' : Number(value)
}

function arrayValue(fieldId: number) {
  return Array.isArray(values[fieldId]) ? values[fieldId] as string[] : []
}

function rangeValue(fieldId: number) {
  const value = values[fieldId]
  if (value && typeof value === 'object') {
    const range = value as { start?: string; end?: string }
    return { start: range.start || '', end: range.end || '' }
  }
  return { start: '', end: '' }
}

function inputType(fieldType?: string) {
  if (fieldType === 'email') return 'email'
  if (fieldType === 'phone') return 'tel'
  if (fieldType === 'url') return 'url'
  return 'text'
}

function priceHint(assignment: ProductComplexFieldAssignment) {
  const field = assignment.field
  if (!field) return ''
  const { pricingMode, pricingAmount, pricingMinUnits, pricingExtraAmount } =
    resolveAssignmentPricing(assignment)
  if (pricingMode === 'none') return ''
  const unit = field.pricingUnitLabel || 'unitate'
  if (pricingMode === 'fixed') {
    if (!pricingAmount) return ''
    return `+${pricingAmount.toFixed(2)} RON`
  }
  if (pricingMode === 'per_unit') {
    if (!pricingAmount) return ''
    return `+${pricingAmount.toFixed(2)} RON / ${unit}`
  }
  if (pricingMode === 'per_unit_tiered') {
    const min = Math.max(0, Math.floor(pricingMinUnits || 0))
    if (min > 0) {
      return `Primele ${min} ${unit}: ${pricingAmount.toFixed(2)} RON, apoi +${pricingExtraAmount.toFixed(2)} RON / ${unit}`
    }
    return `+${pricingExtraAmount.toFixed(2)} RON / ${unit}`
  }
  if (pricingMode === 'per_unit_percent') {
    if (!pricingAmount) return ''
    return `+${pricingAmount}% din prețul final / ${unit}`
  }
  return ''
}

function dateRangeMinDays(assignment: ProductComplexFieldAssignment) {
  if (assignment.field?.fieldType !== 'date_range') return 1
  const { pricingMode, pricingMinUnits } = resolveAssignmentPricing(assignment)
  if (pricingMode !== 'per_unit_tiered') return 1
  return Math.max(1, Math.floor(Number(pricingMinUnits) || 1))
}

function setValue(fieldId: number, value: unknown) {
  values[fieldId] = value
  delete errors[fieldId]
  emitChange()
}

function setRangeValue(
  fieldId: number,
  value: string | { start?: string; end?: string } | null,
) {
  if (!value || typeof value === 'string') {
    values[fieldId] = null
  } else {
    values[fieldId] = {
      start: value.start || '',
      end: value.end || '',
    }
  }
  delete errors[fieldId]
  emitChange()
}

function emitChange() {
  const customFields: CustomFieldValueInput[] = []
  for (const assignment of props.assignments) {
    const field = assignment.field
    if (!field) continue
    const value = values[field.id]
    if (isEmptyCustomFieldValue(value)) continue
    customFields.push({ fieldId: field.id, value })
  }

  const priceDelta = computeCustomFieldsPriceDelta(
    props.assignments,
    values,
    props.basePrice,
  )

  emit('change', customFields, priceDelta)
}

function validate(): boolean {
  let ok = true
  for (const key of Object.keys(errors)) delete errors[Number(key)]

  for (const assignment of props.assignments) {
    const field = assignment.field
    if (!field) continue

    if (assignment.isRequired && isEmptyCustomFieldValue(values[field.id])) {
      errors[field.id] = 'Câmp obligatoriu'
      ok = false
      continue
    }

    const minDays = dateRangeMinDays(assignment)
    if (field.fieldType === 'date_range' && minDays > 1) {
      const units = getFieldUnitCount('date_range', values[field.id])
      if (units > 0 && units < minDays) {
        const unit = field.pricingUnitLabel?.trim() || 'zile'
        errors[field.id] = `Selectează cel puțin ${minDays} ${unit}`
        ok = false
      }
    }
  }
  return ok
}

function getValues(): CustomFieldValueInput[] {
  return props.assignments
    .filter((assignment) => assignment.field && !isEmptyCustomFieldValue(values[assignment.field.id]))
    .map((assignment) => ({
      fieldId: assignment.fieldId,
      value: values[assignment.fieldId],
    }))
}

defineExpose({ validate, getValues })
</script>
